import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { DEPARTMENTS, DEPT_DB_MAP, DEPT_REVERSE_MAP, formatAmount, BASE, authFetch } from '../constants';
import KpiCard from '../components/ui/KpiCard';
import FilterBar from '../components/ui/FilterBar';

// Fixed department order
const DEPT_ORDER = [
  'CFO Office',
  'Finance Operations', 'FP&A', 'Accounting', 'Treasury', 'Tax',
  'Credit & Risk', 'Financial Systems', 'Internal Audit', 'Payroll', 'Procurement',
];

// Department name -> CSS variable
const DEPT_COLOR_VAR: Record<string, string> = {
  'CFO Office': 'var(--dept-cfo)',
  'Finance Operations': 'var(--dept-finops)',
  'FP&A': 'var(--dept-fpa)',
  'Accounting': 'var(--dept-accounting)',
  'Treasury': 'var(--dept-treasury)',
  'Tax': 'var(--dept-tax)',
  'Credit & Risk': 'var(--dept-credit)',
  'Financial Systems': 'var(--dept-finsys)',
  'Internal Audit': 'var(--dept-audit)',
  'Payroll': 'var(--dept-payroll)',
  'Procurement': 'var(--dept-procurement)',
};

// DB department -> display department name (reverse of DEPT_DB_MAP)
const DB_TO_DISPLAY: Record<string, string> = {};
for (const [dbName, displayName] of Object.entries(DEPT_DB_MAP)) {
  DB_TO_DISPLAY[dbName] = displayName;
}

function healthColor(h: number): string {
  return h >= 70 ? 'var(--status-resolved)' : h >= 50 ? 'var(--severity-high)' : 'var(--severity-critical)';
}

function severityBorder(stats: any): string {
  if (!stats) return 'var(--border-subtle)';
  if (stats.escalated > 0) return 'var(--severity-critical)';
  if (stats.flagged > 0) return 'var(--severity-high)';
  if (stats.taskCount > 0 && stats.health < 100) return 'var(--severity-medium)';
  return 'var(--status-resolved)';
}

const METRICS_API = `${BASE}/api/v1/metrics`;

export default function OrgChart() {
  const navigate = useNavigate();
  const {
    orgData, loading, visibleDepartments,
    entity, entities, setEntity,
    role, authUser,
  } = useApp();

  // Live metrics state
  const [commandCenter, setCommandCenter] = useState<any>(null);
  const [pipeline, setPipeline] = useState<any>(null);
  const [deptMetrics, setDeptMetrics] = useState<any>(null);
  const [periods, setPeriods] = useState<string[]>([]);
  const [period, setPeriodLocal] = useState('');
  const [metricsLoading, setMetricsLoading] = useState(true);

  // Activity feed from orgData
  const recentActivity = orgData?.recent_activity || [];

  // Compute DB department names from role's display department names
  const roleDeptDbNames = (() => {
    if (!role.departments || role.departments === 'all') return null;
    const dbNames: string[] = [];
    for (const displayDept of role.departments as string[]) {
      const mapped = DEPT_REVERSE_MAP[displayDept];
      if (mapped) dbNames.push(...mapped);
    }
    return dbNames.length > 0 ? dbNames : null;
  })();

  // Build query params
  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (entity) params.set('entity_id', entity);
    if (period) params.set('period', period);
    params.set('user_level', role.level);
    if (roleDeptDbNames) params.set('departments', roleDeptDbNames.join(','));
    return params.toString();
  }, [entity, period, role.level, roleDeptDbNames]);

  // Fetch all metrics
  const fetchMetrics = useCallback(async () => {
    setMetricsLoading(true);
    const qs = buildParams();
    try {
      const [ccRes, pipRes, deptRes, perRes] = await Promise.all([
        authFetch(`${METRICS_API}/command-center?${qs}`),
        authFetch(`${METRICS_API}/approval-pipeline?${qs}`),
        authFetch(`${METRICS_API}/departments?${qs}`),
        authFetch(`${METRICS_API}/periods`),
      ]);
      if (ccRes.ok) setCommandCenter(await ccRes.json());
      if (pipRes.ok) setPipeline(await pipRes.json());
      if (deptRes.ok) setDeptMetrics(await deptRes.json());
      if (perRes.ok) {
        const data = await perRes.json();
        setPeriods(data.periods || []);
      }
    } catch (err) {
      console.error('Metrics fetch failed:', err);
    }
    setMetricsLoading(false);
  }, [buildParams]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  // Health score from live metrics
  const healthScore = commandCenter?.health_score ?? 0;

  // Department data in fixed order
  const visibleSet = new Set(visibleDepartments);
  const orderedDepts = DEPT_ORDER.filter(d => visibleSet.has(d));
  const cfo = orderedDepts[0] === 'CFO Office' ? orderedDepts[0] : null;
  const row1 = orderedDepts.filter(d => ['Finance Operations', 'FP&A', 'Accounting', 'Treasury', 'Tax'].includes(d));
  const row2 = orderedDepts.filter(d => ['Credit & Risk', 'Financial Systems', 'Internal Audit', 'Payroll', 'Procurement'].includes(d));

  // Map DB department metrics to display departments
  const getDeptLiveStats = (displayDeptName: string) => {
    if (!deptMetrics?.departments) return null;
    const dbDepts: string[] = [];
    for (const [dbName, dispName] of Object.entries(DEPT_DB_MAP)) {
      if (dispName === displayDeptName) dbDepts.push(dbName);
    }
    let taskCount = 0, openExposure = 0, grossValue = 0, resolvedCount = 0, escalated = 0, flagged = 0;
    for (const dept of deptMetrics.departments) {
      if (dbDepts.includes(dept.department)) {
        taskCount += dept.task_count;
        openExposure += dept.open_exposure;
        grossValue += dept.gross_exception_value;
        resolvedCount += dept.resolved_count;
        escalated += dept.escalated;
        flagged += dept.flagged;
      }
    }
    const health = grossValue > 0 ? Math.round((resolvedCount / taskCount) * 100) : 100;
    return { taskCount, openExposure, grossValue, resolvedCount, escalated, flagged, health };
  };

  // Greeting
  const userName = authUser ? (authUser.full_name || '').split(/\s+/)[0] : 'User';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const pendingCount = commandCenter?.pending_my_approval?.count || commandCenter?.open_exposure?.count || 0;

  if (loading && metricsLoading) {
    return <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-secondary)' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1600, margin: '0 auto' }}>
      {/* Greeting */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>
          {greeting}, {userName}.
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
          {pendingCount > 0 ? (
            <><span style={{ color: 'var(--accent)', fontWeight: 600 }}>{pendingCount} items</span> need your attention</>
          ) : (
            <>All clear — no items need your attention</>
          )}
        </div>
      </div>

      {/* Controls row */}
      <FilterBar filters={[
        {
          key: 'entity', label: 'Entity', type: 'select', value: entity,
          onChange: v => setEntity(v),
          options: entities.map((ent: any) => ({ value: ent.entity_code, label: `${ent.entity_code} \u2014 ${ent.entity_name}` })),
          placeholder: 'All Entities',
        },
        {
          key: 'period', label: 'Period', type: 'select', value: period,
          onChange: v => setPeriodLocal(v),
          options: periods.map(p => ({ value: p, label: p })),
          placeholder: 'Period',
        },
      ]} />

      {/* Health Score bar */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '16px 20px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: '0.95rem', fontWeight: 600 }}>Organization Health Score</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.5rem', fontWeight: 700, color: healthScore === 0 && (commandCenter?.recovery_value?.count || 0) === 0 ? 'var(--text-muted)' : healthColor(healthScore) }}>
            {healthScore === 0 && (commandCenter?.recovery_value?.count || 0) === 0 ? 'Pending' : `${healthScore.toFixed(1)}%`}
          </span>
        </div>
        <div style={{ height: 8, background: 'var(--bg-elevated)', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${Math.min(healthScore, 100)}%`, background: healthColor(healthScore), borderRadius: 4, transition: 'width 0.5s ease' }} />
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-hint)', marginTop: 6 }}>
          Confirmed Adjustments / Gross Exception Value
        </div>
      </div>

      {/* 5 KPI boxes */}
      {commandCenter && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { value: formatAmount(commandCenter.open_exposure.value), count: `${commandCenter.open_exposure.count} exceptions`, label: 'OPEN EXPOSURE', color: 'var(--severity-high)', onClick: () => navigate('/operations') },
            { value: formatAmount(commandCenter.pending_my_approval.value), count: `${commandCenter.pending_my_approval.count} exceptions`, label: 'PENDING MY APPROVAL', color: 'var(--severity-critical)', onClick: () => navigate('/operations') },
            { value: formatAmount(commandCenter.recovery_value.value), count: `${commandCenter.recovery_value.count} resolved`, label: 'RECOVERY VALUE', color: 'var(--status-resolved)', onClick: () => navigate('/activity-tracker') },
            { value: `${commandCenter.exception_rate.value.toFixed(1)}%`, count: `${commandCenter.exception_rate.count} of ${commandCenter.exception_rate.total_transactions}`, label: 'EXCEPTION RATE', color: 'var(--blue)', onClick: () => navigate('/activity-tracker') },
            { value: formatAmount(commandCenter.aging_over_7d.value), count: `${commandCenter.aging_over_7d.count} exceptions`, label: 'AGING > 7 DAYS', color: commandCenter.aging_over_7d.count > 0 ? 'var(--severity-critical)' : 'var(--text-hint)', onClick: () => navigate('/activity-tracker') },
          ].map((kpi) => (
            <KpiCard
              key={kpi.label}
              label={kpi.label}
              value={kpi.value}
              delta={kpi.count}
              valueColor={kpi.color}
              onClick={kpi.onClick}
            />
          ))}
        </div>
      )}

      {/* Approval Pipeline */}
      {pipeline && pipeline.levels && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '14px 20px', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase' as const, letterSpacing: '0.8px' }}>
              Approval Pipeline
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
              {pipeline.total_approved} approved / {pipeline.total_pending} pending / {pipeline.total_in_workflow} total
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            {pipeline.levels.map((lv: any) => {
              const maxTotal = Math.max(...pipeline.levels.map((l: any) => l.total), 1);
              const approvedH = lv.approved > 0 ? Math.max(6, (lv.approved / maxTotal) * 60) : 0;
              const pendingH = lv.pending > 0 ? Math.max(6, (lv.pending / maxTotal) * 60) : 0;
              const isMyLevel = `L${lv.level}` === role.level;
              return (
                <div key={lv.level} style={{ flex: 1, textAlign: 'center', borderBottom: isMyLevel ? '2px solid var(--accent)' : '2px solid transparent', paddingBottom: 4 }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                    {lv.total > 0 ? lv.total : '-'}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1, margin: '0 auto', width: '70%' }}>
                    {lv.pending > 0 && (
                      <div style={{ height: pendingH, background: 'var(--severity-high)', borderRadius: '3px 3px 0 0', transition: 'height 0.3s' }}
                        title={`${lv.pending} pending`} />
                    )}
                    {lv.approved > 0 && (
                      <div style={{ height: approvedH, background: 'var(--status-resolved)', borderRadius: lv.pending > 0 ? '0 0 3px 3px' : 3, transition: 'height 0.3s' }}
                        title={`${lv.approved} approved`} />
                    )}
                    {lv.total === 0 && (
                      <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 3 }} />
                    )}
                  </div>
                  <div style={{ fontSize: 9, color: isMyLevel ? 'var(--accent)' : 'var(--text-hint)', fontWeight: 600, marginTop: 4 }}>L{lv.level}</div>
                  <div style={{ fontSize: 8, fontFamily: 'var(--font-mono)' }}>
                    {lv.approved > 0 && <span style={{ color: 'var(--status-resolved)' }}>{lv.approved}</span>}
                    {lv.approved > 0 && lv.pending > 0 && <span style={{ color: 'var(--text-hint)' }}>/</span>}
                    {lv.pending > 0 && <span style={{ color: 'var(--severity-high)' }}>{lv.pending}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Department grid */}
      <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.8px', color: 'var(--text-hint)', marginBottom: 14 }}>Departments</div>

      {cfo && (
        <div className="dgrid" style={{ marginBottom: 14 }}>
          <DeptCard name={cfo} liveStats={getDeptLiveStats(cfo)} index={0} navigate={navigate} isHero />
        </div>
      )}

      {row1.length > 0 && (
        <div className="dgrid">
          {row1.map((d, i) => (
            <DeptCard key={d} name={d} liveStats={getDeptLiveStats(d)} index={i + 1} navigate={navigate} />
          ))}
        </div>
      )}

      {row2.length > 0 && (
        <div className="dgrid">
          {row2.map((d, i) => (
            <DeptCard key={d} name={d} liveStats={getDeptLiveStats(d)} index={i + 6} navigate={navigate} />
          ))}
        </div>
      )}

      {/* Activity feed */}
      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%', background: 'var(--severity-critical)',
            animation: 'pulse 1.5s infinite', flexShrink: 0,
          }} />
          <span style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.8px', color: 'var(--text-hint)' }}>Live Activity</span>
        </div>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          {recentActivity.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              No AI processing yet. Activity will appear here as agents process tasks.
            </div>
          ) : (
            recentActivity.slice(0, 8).map((item: any, i: number) => (
              <div key={i}
                onClick={() => item.exception_id && navigate(`/activity-tracker/${item.exception_id}`)}
                style={{
                  display: 'flex', alignItems: 'center', padding: '10px 16px',
                  borderBottom: '1px solid var(--border-subtle)', fontSize: '0.82rem',
                  cursor: item.exception_id ? 'pointer' : 'default',
                  opacity: 0, animation: `fadeIn 0.4s ease forwards`,
                  animationDelay: `${0.35 + i * 0.07}s`,
                  transition: 'background var(--duration-fast)',
                }}
                onMouseEnter={e => { if (item.exception_id) (e.currentTarget as HTMLElement).style.background = 'var(--bg-elevated)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <div style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: activityDotColor(item),
                  marginRight: 12, flexShrink: 0,
                }} />
                <span style={{ fontWeight: 500, marginRight: 6, color: 'var(--text-primary)' }}>
                  {activityLabel(item)}
                </span>
                <span style={{ color: 'var(--text-secondary)', flex: 1 }}>
                  {item.title || `#${item.exception_id}`}
                  {item.amount ? ` \u2014 ${formatAmount(item.amount)}` : ''}
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--text-hint)', marginLeft: 16 }}>
                  {timeAgo(item.processed_at || item.created_at)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* -- Helper components -- */

function DeptCard({ name, liveStats, index, navigate, isHero }: {
  name: string; liveStats: any; index: number; navigate: any; isHero?: boolean;
}) {
  const deptConfig = DEPARTMENTS[name];
  if (!deptConfig) return null;
  const color = DEPT_COLOR_VAR[name] || deptConfig.color;
  const stats = liveStats || { taskCount: 0, openExposure: 0, grossValue: 0, resolvedCount: 0, escalated: 0, flagged: 0, health: 100 };
  const h = stats.health;
  const borderColor = severityBorder(stats);
  const isActive = stats.taskCount > 0 && h < 100;

  return (
    <div
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-default)',
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: 14,
        padding: '16px 18px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        opacity: 0,
        animation: `fadeIn 0.4s var(--ease-default) both`,
        animationDelay: `${index * 0.05}s`,
        minHeight: isHero ? undefined : 160,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        ...(isHero ? { borderTop: `3px solid var(--accent)` } : {}),
      }}
      onClick={() => navigate(`/department/${deptConfig.slug}`)}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = 'var(--border-hover)'; e.currentTarget.style.borderLeftColor = borderColor; if (isHero) e.currentTarget.style.borderTopColor = 'var(--accent)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.borderLeftColor = borderColor; if (isHero) e.currentTarget.style.borderTopColor = 'var(--accent)'; }}
    >
      {/* Header: name + breathing dot + health */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            width: 5, height: 5, borderRadius: '50%', background: 'var(--status-resolved)',
            animation: 'breathe 2.5s ease-in-out infinite', flexShrink: 0,
          }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{name}</span>
        </div>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', fontWeight: 700, color: healthColor(h) }}>{h}%</span>
      </div>

      {/* Progress bar with scanline */}
      <div style={{ height: 3, background: 'rgba(0,0,0,0.05)', borderRadius: 2, marginBottom: 12, overflow: 'hidden', position: 'relative' as const }}>
        <div style={{ height: '100%', width: `${h}%`, background: healthColor(h), borderRadius: 2, transition: 'width 0.5s ease' }} />
        {isActive && (
          <div style={{
            position: 'absolute' as const, top: 0, width: '30%', height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.08), transparent)',
            animation: 'scanline 3s linear infinite',
          }} />
        )}
      </div>

      {/* Hero exposure amount */}
      <div style={{
        fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 15, marginBottom: 10,
        color: stats.openExposure > 0 ? 'var(--severity-high)' : 'var(--text-hint)',
      }}>
        {stats.openExposure > 0 ? formatAmount(stats.openExposure) : '\u2014'}
      </div>

      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 16px', fontSize: 11 }}>
        <StatRow label="Tasks" value={stats.taskCount} />
        <StatRow label="Resolved" value={stats.resolvedCount} color={stats.resolvedCount > 0 ? 'var(--status-resolved)' : undefined} />
        <StatRow label="Flagged" value={stats.flagged} color={stats.flagged > 0 ? 'var(--severity-high)' : undefined} />
        <StatRow label="Escalated" value={stats.escalated} color={stats.escalated > 0 ? 'var(--severity-critical)' : undefined} />
      </div>
    </div>
  );
}

function StatRow({ label, value, color }: { label: string; value: string | number; color?: string }) {
  const v = typeof value === 'number' ? value.toLocaleString() : value;
  const displayColor = color || (v === '0' || value === 0 ? 'var(--text-hint)' : 'var(--text-primary)');
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 11, color: displayColor }}>{v}</span>
    </div>
  );
}

function activityDotColor(item: any): string {
  const status = (item.status || '').toLowerCase();
  if (status.startsWith('resolved')) return 'var(--status-resolved)';
  if (status === 'escalated') return 'var(--severity-high)';
  if (status === 'processing') return 'var(--accent)';
  return 'var(--blue)';
}

function activityLabel(item: any): string {
  const action = (item.recommended_action || item.action || '').toLowerCase();
  if (action.includes('approve')) return 'Exception Detection';
  if (action.includes('investigate')) return 'Investigation';
  if (action.includes('escalat')) return 'Escalation';
  const dept = item.department || '';
  if (dept) {
    const display = DB_TO_DISPLAY[dept];
    if (display) return display;
    return dept.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
  }
  return 'Exception Detection';
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
